import { useCallback, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  getAdminStore,
  getWebsiteTemplatePreviewRenderData,
  websiteTemplatesAdminKeys,
  type SupabaseClient,
  type WebsitePageKind,
  type WebsiteTemplateCatalogItem,
} from '@dukanti/shared';
import { env } from '@dukanti/shared/env';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { useStorefrontCanvasHost, type CanvasPageData } from '../hooks/use-storefront-canvas-host';
import { useWebsiteT } from '../hooks/use-website-t';
import { resolveCanvasOrigins, type CanvasOrigins } from '../lib/admin-origins';
import type { FrameDevice } from '../lib/frame-viewport';
import { toCanvasStoreIdentity } from '../lib/store-identity';
import { DeviceToggle } from './device-toggle';
import { StorefrontFrame } from './storefront-frame';

export interface TemplateDemoDialogProps {
  readonly client: SupabaseClient;
  readonly storeId: string;
  readonly storeSlug: string;
  readonly item: WebsiteTemplateCatalogItem;
  readonly onUse: (item: WebsiteTemplateCatalogItem) => void;
  readonly onClose: () => void;
  readonly origins?: CanvasOrigins | null;
}

const DEMO_PAGES: readonly { readonly kind: WebsitePageKind; readonly key: string }[] = [
  { kind: 'home', key: 'admin.websiteTemplates.editor.page.home' },
  { kind: 'catalog', key: 'admin.websiteTemplates.editor.page.catalog' },
  { kind: 'product', key: 'admin.websiteTemplates.editor.page.product' },
  { kind: 'cart', key: 'admin.websiteTemplates.editor.page.cart' },
];

/**
 * The gallery Preview: a template nobody has chosen yet, with sample
 * products, in the same frame the merchant's own storefront uses. It shows
 * Home, Products, Product and Cart, holds no grant, no regions and no edit
 * mode, and changes nothing. This is the ONLY place fixtures are allowed.
 */
export function TemplateDemoDialog({
  client,
  storeId,
  storeSlug,
  item,
  onUse,
  onClose,
  origins: originsProp,
}: TemplateDemoDialogProps): React.JSX.Element {
  const { t, lang } = useWebsiteT();
  const queryClient = useQueryClient();
  const [device, setDevice] = useState<FrameDevice>('desktop');
  const returnFocusRef = useRef<{ target: HTMLButtonElement; href: string } | null>(null);
  const dismissedRef = useRef(false);
  const origins = useMemo(
    () =>
      originsProp !== undefined
        ? originsProp
        : resolveCanvasOrigins({
            currentOrigin: window.location.origin,
            publicAdminUrl: env.VITE_PUBLIC_ADMIN_URL,
            publicStorefrontUrl: env.VITE_PUBLIC_STOREFRONT_URL,
          }),
    [originsProp],
  );

  const loadPage = useCallback(
    async (pageKind: WebsitePageKind): Promise<CanvasPageData> => {
      const [dto, store] = await Promise.all([
        queryClient.fetchQuery({
          queryKey: websiteTemplatesAdminKeys.templatePreview(
            storeId,
            item.currentVersionId,
            pageKind,
          ),
          queryFn: () =>
            getWebsiteTemplatePreviewRenderData(client, {
              storeId,
              templateVersionId: item.currentVersionId,
              pageKind,
            }),
          staleTime: 60_000,
        }),
        queryClient.ensureQueryData({
          queryKey: ['store', storeId],
          queryFn: () => getAdminStore(client, storeId),
        }),
      ]);
      return { dto, store: toCanvasStoreIdentity(store, env.VITE_SUPABASE_URL) };
    },
    [client, item.currentVersionId, queryClient, storeId],
  );

  const host = useStorefrontCanvasHost({
    storeSlug,
    origins,
    mode: 'template-demo',
    adminLocale: lang,
    overlayLabels: {
      moreItems: t('admin.websiteTemplates.overlay.moreItems'),
      regionListTitle: t('admin.websiteTemplates.overlay.regionListTitle'),
    },
    editMode: false,
    locked: false,
    contentKey: item.currentVersionId,
    initialPageKind: 'home',
    loadPage,
    regionsFor: () => [],
  });

  const name = t(item.titleKey);
  const current = host.route.pageKind ?? 'home';
  const dismiss = (): void => {
    dismissedRef.current = true;
    onClose();
  };

  return (
    <Dialog open onOpenChange={(next) => (next ? undefined : dismiss())}>
      <DialogContent
        data-testid="template-demo-dialog"
        className="flex h-[min(85vh,52rem)] max-w-5xl flex-col gap-3"
        onOpenAutoFocus={() => {
          // The library conditionally mounts this dialog without a Radix Trigger.
          // Capture the real opener before the native focus trap moves focus.
          const target = document.activeElement;
          returnFocusRef.current =
            target instanceof HTMLButtonElement ? { target, href: window.location.href } : null;
        }}
        onCloseAutoFocus={(event) => {
          // The native lifecycle runs after conditional unmount. Do not restore
          // during a Use-template handoff, navigation or unrelated removal.
          event.preventDefault();
          const opener = returnFocusRef.current;
          if (!dismissedRef.current || !opener) return;
          const { target, href } = opener;
          if (
            !target.isConnected ||
            target.matches(':disabled, [aria-disabled="true"]') ||
            target.closest('[inert]') ||
            window.location.href !== href
          )
            return;
          const active = target.ownerDocument.activeElement;
          if (
            active !== target.ownerDocument.body &&
            active !== target &&
            !(event.target instanceof HTMLElement && event.target.contains(active))
          )
            return;
          target.focus({ preventScroll: true });
        }}
      >
        <DialogHeader>
          <DialogTitle>{t('admin.websiteTemplates.preview.title', { name })}</DialogTitle>
          <DialogDescription>{t('admin.websiteTemplates.preview.readOnly')}</DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap items-center justify-between gap-2">
          <div
            role="tablist"
            aria-label={t('admin.websiteTemplates.preview.title', { name })}
            className="flex flex-wrap gap-1.5"
          >
            {DEMO_PAGES.map(({ kind, key }) => (
              <button
                key={kind}
                type="button"
                role="tab"
                aria-selected={current === kind}
                data-testid={`demo-tab-${kind}`}
                onClick={() => host.navigate({ kind: 'page', pageKind: kind })}
                className={cn(
                  'focus-visible:ring-ring/50 rounded-full border px-3 py-1 text-sm outline-none transition-colors focus-visible:ring',
                  current === kind
                    ? 'border-admin-lime bg-admin-lime text-primary-foreground'
                    : 'border-input bg-card hover:bg-admin-surface-muted',
                )}
              >
                {t(key)}
              </button>
            ))}
          </div>
          <DeviceToggle device={device} onChange={setDevice} />
        </div>

        <div className="border-border relative flex min-h-0 flex-1 overflow-hidden rounded-lg border">
          <StorefrontFrame host={host} device={device} title={name} />
        </div>

        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={dismiss}>
            {t('admin.websiteTemplates.common.close')}
          </Button>
          <Button
            type="button"
            onClick={() => {
              dismissedRef.current = false;
              onClose();
              onUse(item);
            }}
          >
            {t('admin.websiteTemplates.preview.useThis')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
